import React, { useState } from "react";
import {
  ScheduleEntry,
  WeekSchedule,
  MAX_WINDOWS_PER_DAY,
} from "../../services/schedule";
import Button from "./Button";

type Props = {
  value: WeekSchedule;
  schedulerActive: boolean;
  onChange: (next: WeekSchedule, schedulerActive: boolean) => void;
  disabled?: boolean;
};

// UI day order: 0=Mon … 6=Sun.
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function toInputValue(h: number, m: number): string {
  return `${pad2(h)}:${pad2(m)}`;
}

function fromInputValue(v: string): { h: number; m: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(v);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return { h, m };
}

// Minutes of usable (unlocked) time, wrapping past midnight.
function windowMinutes(w: ScheduleEntry): number {
  const start = w.startHour * 60 + w.startMinute;
  const end = w.endHour * 60 + w.endMinute;
  return end >= start ? end - start : 1440 - start + end;
}

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function cloneSchedule(s: WeekSchedule): WeekSchedule {
  const out: WeekSchedule = {};
  for (let i = 0; i < 7; i++) {
    out[i] = (s[i] ?? []).map((w) => ({ ...w }));
  }
  return out;
}

// Green (unlocked) segments for one day's timeline, as percentage spans.
// A window that wraps past midnight is unlocked from its start until midnight on
// its own day, then from midnight until its end on the NEXT day, so we draw the
// after-midnight half on the following day rather than back on the same one.
function unlockedSpans(
  value: WeekSchedule,
  day: number
): Array<{ left: number; width: number }> {
  const spans: Array<{ left: number; width: number }> = [];
  const pct = (mins: number) => (mins / 1440) * 100;

  // Windows that start on this day.
  for (const w of value[day] ?? []) {
    const start = w.startHour * 60 + w.startMinute;
    const end = w.endHour * 60 + w.endMinute;
    if (end > start) {
      spans.push({ left: pct(start), width: pct(end - start) });
    } else {
      // Wraps past midnight: show only up to midnight here.
      spans.push({ left: pct(start), width: pct(1440 - start) });
    }
  }

  // Carry-over: the previous day's wrapping window covers this day's early hours.
  const prevDay = (day + 6) % 7;
  for (const w of value[prevDay] ?? []) {
    const start = w.startHour * 60 + w.startMinute;
    const end = w.endHour * 60 + w.endMinute;
    if (end <= start && end > 0) {
      spans.push({ left: 0, width: pct(end) });
    }
  }
  return spans;
}

export default function ScheduleEditor({
  value,
  schedulerActive,
  onChange,
  disabled,
}: Props) {
  const [selectedDay, setSelectedDay] = useState(0);
  const [copyMode, setCopyMode] = useState(false);
  const [copyTargets, setCopyTargets] = useState<Set<number>>(new Set());

  const dayWindows = value[selectedDay] ?? [];
  const anyScheduled = Object.keys(value).some(
    (k) => (value[Number(k)] ?? []).length > 0
  );

  const emitSchedule = (next: WeekSchedule) => onChange(next, schedulerActive);

  const updateWindow = (
    uiDay: number,
    idx: number,
    patch: Partial<ScheduleEntry>
  ) => {
    const next = cloneSchedule(value);
    next[uiDay][idx] = { ...next[uiDay][idx], ...patch };
    emitSchedule(next);
  };

  const addWindow = (uiDay: number) => {
    if ((value[uiDay] ?? []).length >= MAX_WINDOWS_PER_DAY) return;
    const next = cloneSchedule(value);
    next[uiDay].push({ startHour: 9, startMinute: 0, endHour: 17, endMinute: 0 });
    emitSchedule(next);
  };

  const deleteWindow = (uiDay: number, idx: number) => {
    const next = cloneSchedule(value);
    next[uiDay].splice(idx, 1);
    emitSchedule(next);
  };

  const startCopy = () => {
    setCopyMode(true);
    setCopyTargets(new Set());
  };

  const cancelCopy = () => {
    setCopyMode(false);
    setCopyTargets(new Set());
  };

  const toggleCopyTarget = (uiDay: number) => {
    if (uiDay === selectedDay) return;
    const next = new Set(copyTargets);
    if (next.has(uiDay)) next.delete(uiDay);
    else next.add(uiDay);
    setCopyTargets(next);
  };

  const applyCopy = () => {
    if (copyTargets.size === 0) {
      cancelCopy();
      return;
    }
    const next = cloneSchedule(value);
    const source = (value[selectedDay] ?? []).map((w) => ({ ...w }));
    copyTargets.forEach((day) => {
      next[day] = source.map((w) => ({ ...w }));
    });
    emitSchedule(next);
    cancelCopy();
  };

  const onDayClick = (uiDay: number) => {
    if (copyMode) toggleCopyTarget(uiDay);
    else setSelectedDay(uiDay);
  };

  const timeInputClass =
    "bg-white border border-black/15 rounded-control px-2 py-1 text-sm text-ink focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 disabled:opacity-50";

  return (
    <div className={disabled ? "opacity-60 pointer-events-none" : ""}>
      {/* Master enable toggle */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="font-semibold text-ink">
            {schedulerActive ? "Schedule Enabled" : "Schedule Disabled"}
          </div>
          <div className="text-ink-3 text-xs mt-0.5">
            {schedulerActive
              ? "The stove follows the weekly schedule below."
              : "The schedule is saved but not enforced."}
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={schedulerActive}
          onClick={() => onChange(value, !schedulerActive)}
          className={[
            "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
            schedulerActive ? "bg-primary" : "bg-black/20",
          ].join(" ")}
        >
          <span
            className={[
              "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform",
              schedulerActive ? "translate-x-5" : "translate-x-0.5",
            ].join(" ")}
          />
        </button>
      </div>

      {/* Day selector */}
      <div className="flex flex-wrap gap-2 mb-4">
        {DAY_LABELS.map((label, uiDay) => {
          const hasWindows = (value[uiDay] ?? []).length > 0;
          const isSelected = !copyMode && uiDay === selectedDay;
          const isTarget = copyMode && copyTargets.has(uiDay);
          const isSource = copyMode && uiDay === selectedDay;
          return (
            <button
              key={label}
              type="button"
              onClick={() => onDayClick(uiDay)}
              className={[
                "relative px-3 py-1.5 rounded-control text-sm font-medium border transition-colors",
                isSelected
                  ? "bg-ink text-white border-ink"
                  : isTarget
                    ? "bg-primary/20 text-ink border-primary"
                    : isSource
                      ? "bg-black/10 text-ink border-black/30"
                      : "bg-black/[0.04] text-ink-2 border-black/10 hover:bg-black/[0.08]",
              ].join(" ")}
            >
              {label}
              {hasWindows && (
                <span
                  className={[
                    "absolute -top-1 -right-1 h-2 w-2 rounded-full",
                    isSelected ? "bg-primary" : "bg-alert-success",
                  ].join(" ")}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Copy banner */}
      {copyMode ? (
        <div className="flex items-center justify-between gap-3 mb-4 rounded-control bg-primary/10 border border-primary/40 px-3 py-2">
          <span className="text-sm text-ink">
            Pick days to copy <b>{DAY_LABELS[selectedDay]}</b> to, then Paste.
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={cancelCopy}
              className="!text-ink !border-black/15 hover:!bg-black/[0.04]"
            >
              Cancel
            </Button>
            <Button size="sm" variant="primary" onClick={applyCopy}>
              Paste ({copyTargets.size})
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-ink">{DAY_LABELS[selectedDay]}</h3>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={startCopy}
              disabled={dayWindows.length === 0}
              className="!text-ink !border-black/15 hover:!bg-black/[0.04] disabled:!opacity-40"
            >
              Copy Day
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => addWindow(selectedDay)}
              disabled={dayWindows.length >= MAX_WINDOWS_PER_DAY}
            >
              Add Window
            </Button>
          </div>
        </div>
      )}

      {/* 24h timeline preview. Base is locked (blue) when a schedule exists; with
          no schedule at all the stove is unlocked all day, so the base is green. */}
      <div className="mb-4">
        <div
          className={[
            "relative h-3 w-full rounded-full overflow-hidden",
            anyScheduled ? "bg-alert-info/70" : "bg-alert-success",
          ].join(" ")}
        >
          {unlockedSpans(value, selectedDay).map((s, i) => (
            <div
              key={i}
              className="absolute top-0 h-full bg-alert-success"
              style={{ left: `${s.left}%`, width: `${s.width}%` }}
            />
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-ink-3 mt-1">
          <span>12am</span>
          <span>6am</span>
          <span>12pm</span>
          <span>6pm</span>
          <span>12am</span>
        </div>
      </div>

      {/* Window rows */}
      {dayWindows.length === 0 ? (
        <div className="text-ink-3 text-sm text-center py-6 rounded-control border border-dashed border-black/15">
          {anyScheduled
            ? "No windows. Stove locked all day."
            : "No schedule. Stove unlocked all day."}
        </div>
      ) : (
        <div className="space-y-2">
          {dayWindows.map((w, idx) => (
            <div
              key={idx}
              className="flex items-center gap-3 rounded-control border border-black/10 bg-black/[0.02] px-3 py-2"
            >
              <label className="flex items-center gap-2 text-sm">
                <span className="text-alert-success font-medium">Unlock</span>
                <input
                  type="time"
                  className={timeInputClass}
                  value={toInputValue(w.startHour, w.startMinute)}
                  max={toInputValue(w.endHour, w.endMinute)}
                  onChange={(e) => {
                    const t = fromInputValue(e.target.value);
                    if (!t) return;
                    // Unlock must stay before the shut-off/lock time.
                    if (
                      t.h * 60 + t.m >=
                      w.endHour * 60 + w.endMinute
                    )
                      return;
                    updateWindow(selectedDay, idx, {
                      startHour: t.h,
                      startMinute: t.m,
                    });
                  }}
                />
              </label>
              <label className="flex items-center gap-2 text-sm">
                <span className="text-alert-info font-medium">
                  Disable &amp; Lock
                </span>
                <input
                  type="time"
                  className={timeInputClass}
                  value={toInputValue(w.endHour, w.endMinute)}
                  min={toInputValue(w.startHour, w.startMinute)}
                  onChange={(e) => {
                    const t = fromInputValue(e.target.value);
                    if (!t) return;
                    // Shut-off/lock must be after the unlock time.
                    if (
                      t.h * 60 + t.m <=
                      w.startHour * 60 + w.startMinute
                    )
                      return;
                    updateWindow(selectedDay, idx, {
                      endHour: t.h,
                      endMinute: t.m,
                    });
                  }}
                />
              </label>
              <span className="ml-auto text-xs font-medium text-ink-2 rounded-chip bg-black/[0.06] px-2 py-1">
                {formatDuration(windowMinutes(w))}
              </span>
              <button
                type="button"
                aria-label="Delete window"
                title="Delete window"
                onClick={() => deleteWindow(selectedDay, idx)}
                className="text-alert-critical hover:bg-alert-critical/10 rounded-control w-7 h-7 inline-flex items-center justify-center"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
